import { useMemo } from 'react';
import { type Location, useLocation, useParams } from 'react-router';

import { type PageTitlesTranslation } from '@/assets/locales/pageTitles/pageTitles.translation.en.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import {
  type BreadcrumbItem,
  buildBreadcrumbTrail,
} from '@/layouts/private/topBar/breadcrumbs/buildBreadcrumbTrail.util.ts';
import { useEntityBreadcrumbName } from '@/layouts/private/topBar/breadcrumbs/useEntityBreadcrumbName.util.ts';
import { type RouteEnum, type RouteModel } from '@/layouts/routes/Route.model.ts';
import { ROUTES } from '@/layouts/routes/routes';
import { type RouteStateModel } from '@/model/RouteState.model.ts';

const ROUTES_BY_PATH = new Map<RouteEnum, RouteModel>(ROUTES.map(route => [route.path, route]));

export type ResolvedBreadcrumbItem = BreadcrumbItem & {
  /** Entity display name resolved from the query cache (OCD-311); renderers fall back to `title` when absent. */
  entityName?: string;
  title: PageTitlesTranslation[keyof PageTitlesTranslation];
};

/**
 * Resolves the breadcrumb trail for the current location (OCD-305).
 * Applies route-level `dynamicTitle` overrides (copy/edit vacancy, privacy
 * policy variant) without reintroducing imperative page-title state. The real
 * router location is forwarded so state-dependent titles (e.g. copyMode) see
 * the same `location.state` the view does. The leaf item additionally carries
 * the entity's display name read from the query cache (OCD-311) — only the
 * last element of the trail is entity-scoped.
 *
 * Runs inside the pathless AppLayout, above the matched leaf route: `useParams`
 * still yields the leaf's params because react-router accumulates them down
 * the match branch.
 */
export const useBreadcrumbs = (): ResolvedBreadcrumbItem[] => {
  const location = useLocation();
  const params = useParams();
  const { pathname } = location;
  const locationState: unknown = location.state;
  const {
    user: { privacyPolicyStatus },
  } = useUserContext();

  const trail = useMemo(
    () => buildBreadcrumbTrail(ROUTES, pathname, locationState),
    [pathname, locationState],
  );

  const leaf = trail.at(-1);
  const leafEntityName = leaf
    ? ROUTES_BY_PATH.get(leaf.routePath)?.breadcrumb?.entityName
    : undefined;
  const leafEntityId = leafEntityName ? params[leafEntityName.param] : undefined;
  const entityName = useEntityBreadcrumbName(leafEntityName, leafEntityId);

  return useMemo(
    () =>
      trail.map((item, index) => {
        const dynamicTitle = ROUTES_BY_PATH.get(item.routePath)?.breadcrumb?.dynamicTitle;
        const routeState: RouteStateModel = {
          location: location as Location<Record<string, unknown>>,
          params: {},
        };
        const title = dynamicTitle ? dynamicTitle(routeState, { privacyPolicyStatus }) : item.label;
        return {
          ...item,
          title,
          ...(index === trail.length - 1 && entityName ? { entityName } : {}),
        };
      }),
    [trail, location, privacyPolicyStatus, entityName],
  );
};
